import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { createStyles, withStyles } from '@mui/styles'
import { api } from '../../../infrastructure/api'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import guid, { Guid } from '../../../infrastructure/guid'
import { t } from '../../../infrastructure/i18nextHelper'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { ColumnDescriptor, DatePicker, Select, TableItem, TextField } from '../../common/customComponents'
import { createExcelLines, MasterDataItem, MasterDataShell } from './masterDataShell'
import { UserSettingsContextContainer } from '../../../infrastructure/shell/userSettings/userSettingsStore'

const translationBase = 'admin.masterdata.publicPipeLineCalendar.'
const apiBase = 'masterdata/publicPipeLineCalendar'

type PublicPipeLineCalendar = {
    id: Guid
    pipeLineNetwork: string
    externalCodeSite: string
    site: string | null
    externalCodeProduct: string
    product: string | null
    cycleId: string
    date: Date
    cycleStatus: string
    changedAt: Date
    changedBy: string
    batchNumber: string | null
}

const fields: (keyof PublicPipeLineCalendar)[] = [
    'pipeLineNetwork', 'externalCodeSite', 'site', 'externalCodeProduct', 'product', 'cycleId',
    'date', 'cycleStatus', 'changedAt', 'changedBy', 'batchNumber'
]

const emptyRow = (): PublicPipeLineCalendar => ({
    id: guid.empty,
    pipeLineNetwork: '',
    externalCodeSite: '',
    site: '',
    externalCodeProduct: '',
    product: '',
    cycleId: '',
    date: new Date(),
    cycleStatus: 'N',
    changedAt: new Date(),
    changedBy: '',
    batchNumber: ''
})

function PublicPipeLineCalendarMasterData({ classes }: MuiProps) {
    const excelGenerator = ExcelGeneratorContainer.useContainer()
    const settings = UserSettingsContextContainer.useContainer()
    const isManager = hasClaim(Claims.MasterdataPublicPipeLineCalendarManager)
    const dateFormat = settings.getDateFormat()
    const [calendars, setCalendars] = useState<PublicPipeLineCalendar[]>([])
    const [selectedRows, setSelectedRows] = useState<TableItem<MasterDataItem<PublicPipeLineCalendar>>[]>([])
    const [filters, setFilters] = useState<Partial<Record<keyof PublicPipeLineCalendar, string | null>>>({})

    const load = async () => {
        const values = await api.get<PublicPipeLineCalendar[]>(apiBase)
        setCalendars(values)
        setSelectedRows([])
    }

    const displayValue = (row: PublicPipeLineCalendar, field: keyof PublicPipeLineCalendar) =>
        field === 'date' || field === 'changedAt'
            ? row[field] == null ? '' : moment(row[field]).format(field === 'changedAt' ? `${dateFormat} h:mm A` : dateFormat)
            : row[field] ?? ''

    const columns: ColumnDescriptor<TableItem<MasterDataItem<PublicPipeLineCalendar>>>[] = fields.map(field => ({
        name: t(translationBase + field),
        value: item => displayValue(item, field),
        columnFilter: {
            value: filters[field] ?? '',
            onChange: value => setFilters(previous => ({ ...previous, [field]: value }))
        }
    }))

    const items = calendars
        .filter(row => fields.every(field =>
            displayValue(row, field).toLowerCase().includes((filters[field] ?? '').toLowerCase())))
        .map(row => ({
            ...row,
            isModified: false,
            rowStyle: row.cycleStatus === 'O' ? { color: 'red' } : undefined
        }))

    useEffect(() => { load() }, [])

    const onSave = async (item: PublicPipeLineCalendar) => {
        await api.post(apiBase, item)
        await load()
        return true
    }

    const onDelete = async (ids: string[] | null) => {
        if (ids == null || ids.length === 0) return false
        await Promise.all((ids ?? []).map(id => api.del(`${apiBase}/${id}`)))
        await load()
        return true
    }

    const onImportExcel = async (file: Blob) => {
        await api.upload(`${apiBase}/import`, file, 'file', { withReport: 'dialog' })
        await load()
    }

    const onExportExcel = () => {
        const visibleSelectedRows = items.filter(row => selectedRows.some(selected => selected.id === row.id))
        const rowsToExport = visibleSelectedRows.length > 0 ? visibleSelectedRows : items
        excelGenerator.generate({
            filename: 'PublicPipeLineCalendar.xlsx',
            sheets: [{ name: 'Calendar', lines: createExcelLines(rowsToExport, columns) }]
        })
    }

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId='public-pipeline-calendar'
                headerLabel={t(translationBase + 'title')}
                itemLabel={t(translationBase + 'title')}
                isManager={isManager}
                items={items}
                columns={columns}
                onExportExcel={onExportExcel}
                onImportExcel={onImportExcel}
                onNew={emptyRow}
                onDelete={onDelete}
                onSave={onSave}
                onSelectionChange={setSelectedRows}>
                {(selectedItem: PublicPipeLineCalendar, setSelectedItem: (item: PublicPipeLineCalendar) => void) => (
                    <>
                        <TextField label={t(translationBase + 'pipeLineNetwork')}
                            text={selectedItem.pipeLineNetwork}
                            disabled={!!selectedItem.id && selectedItem.id !== guid.empty}
                            onChange={event => setSelectedItem({ ...selectedItem, pipeLineNetwork: event.target.value })} />
                        <TextField label={t(translationBase + 'externalCodeSite')}
                            text={selectedItem.externalCodeSite}
                            disabled={!!selectedItem.id && selectedItem.id !== guid.empty}
                            onChange={event => setSelectedItem({ ...selectedItem, externalCodeSite: event.target.value })} />
                        <TextField label={t(translationBase + 'site')}
                            text={selectedItem.site}
                            onChange={event => setSelectedItem({ ...selectedItem, site: event.target.value })} />
                        <TextField label={t(translationBase + 'externalCodeProduct')}
                            text={selectedItem.externalCodeProduct}
                            disabled={!!selectedItem.id && selectedItem.id !== guid.empty}
                            onChange={event => setSelectedItem({ ...selectedItem, externalCodeProduct: event.target.value })} />
                        <TextField label={t(translationBase + 'product')}
                            text={selectedItem.product}
                            onChange={event => setSelectedItem({ ...selectedItem, product: event.target.value })} />
                        <TextField label={t(translationBase + 'cycleId')}
                            text={selectedItem.cycleId}
                            disabled={!!selectedItem.id && selectedItem.id !== guid.empty}
                            onChange={event => setSelectedItem({ ...selectedItem, cycleId: event.target.value })} />
                        <DatePicker label={t(translationBase + 'date')}
                            date={selectedItem.date}
                            setDate={date => date && setSelectedItem({ ...selectedItem, date })} />
                        <Select label={t(translationBase + 'cycleStatus')}
                            value={selectedItem.cycleStatus}
                            choices={[{ value: 'O', text: 'O' }, { value: 'N', text: 'N' }]}
                            onChange={cycleStatus => setSelectedItem({ ...selectedItem, cycleStatus })} />
                        <TextField label={t(translationBase + 'batchNumber')}
                            text={selectedItem.batchNumber}
                            onChange={event => setSelectedItem({ ...selectedItem, batchNumber: event.target.value })} />
                    </>
                )}
            </MasterDataShell>
        </div>
    )
}

const styles = () => createStyles({ container: { height: '100%' } })

export default withStyles(styles, muiOptions)(PublicPipeLineCalendarMasterData)
